import type { ReactNode } from 'react';

/** Moldura comum: 280×200, decorativa; o movimento vem das classes `ilustracao-*` (globals.css). */
function Ilustracao({ children }: { children: ReactNode }) {
    return (
        <svg viewBox="0 0 280 200" aria-hidden className="mx-auto mb-7 block h-[200px] w-[280px] max-w-full">
            {children}
        </svg>
    );
}

const Chao = () => <ellipse className="fill-label/5" cx="140" cy="184" rx="118" ry="7" />;

const CELULAS = [5, 26, 47].flatMap((x) => [5, 26].map((y) => ({ x, y })));

function Modulo({ x, y }: { x: number; y: number }) {
    return (
        <g transform={`translate(${x} ${y})`} className="ilustracao-sombra">
            <rect className="fill-surface stroke-separator" width="70" height="48" rx="5" />
            {CELULAS.map((c) => (
                <rect key={`${c.x}-${c.y}`} className="fill-fill-strong" x={c.x} y={c.y} width="18" height="17" rx="2" />
            ))}
        </g>
    );
}

const MODULOS_NO_LUGAR = [
    { x: 32, y: 48 },
    { x: 178, y: 48 },
    { x: 32, y: 102 },
    { x: 105, y: 102 },
    { x: 178, y: 102 },
];

/** 404: o arranjo de módulos com um lugar vazio e o módulo que saiu flutuando ao lado. */
export function ModuloQueFalta() {
    return (
        <Ilustracao>
            <Chao />
            <path className="fill-none stroke-label-tertiary/55 [stroke-linecap:round] [stroke-width:2.5]" d="M58 150 L52 182 M222 150 L228 182 M140 150 L140 182" />
            <path className="fill-none stroke-label-tertiary/45 [stroke-linecap:round] [stroke-width:1.5]" d="M52 168 H228" />
            {MODULOS_NO_LUGAR.map((m) => (
                <Modulo key={`${m.x}-${m.y}`} {...m} />
            ))}
            <rect className="ilustracao-respira fill-green/10 stroke-green [stroke-dasharray:5_4] [stroke-width:1.5]" x="105.5" y="48.5" width="69" height="47" rx="5" />
            <g className="ilustracao-flutua">
                <g transform="scale(0.78)">
                    <Modulo x={196} y={-6} />
                </g>
            </g>
        </Ilustracao>
    );
}

const CABO = 'fill-none stroke-label/70 [stroke-linecap:round] [stroke-width:5]';

/** Erro inesperado: o eletroposto em repouso com o plugue solto e o anel verde de onde ele volta. */
export function PlugueSolto() {
    return (
        <Ilustracao>
            <Chao />
            <rect className="ilustracao-sombra fill-surface" x="62" y="22" width="84" height="160" rx="16" />
            <rect className="fill-fill-strong" x="76" y="40" width="56" height="38" rx="8" />
            <path className="fill-none stroke-label-tertiary/45 [stroke-linecap:round] [stroke-width:1.5]" d="M94 59 H114" />
            <circle className="fill-fill-strong" cx="104" cy="31" r="3" />
            <rect className="fill-fill-strong" x="76" y="164" width="56" height="8" rx="4" />
            <circle className="fill-fill-strong stroke-green [stroke-width:2]" cx="104" cy="114" r="20" />
            <circle className="fill-surface" cx="104" cy="114" r="13" />
            <circle className="fill-fill-strong" cx="99" cy="110" r="2.6" />
            <circle className="fill-fill-strong" cx="109" cy="110" r="2.6" />
            <circle className="fill-fill-strong" cx="104" cy="119" r="2.6" />
            <path className={CABO} d="M140 170 C 168 186, 200 186, 214 152" />
            <g className="ilustracao-balanca">
                <path className={CABO} d="M214 152 C 220 138, 214 128, 204 120" />
                <g transform="rotate(-38 196 112)">
                    <rect className="fill-label/80" x="178" y="102" width="34" height="20" rx="6" />
                    <rect className="fill-label/80" x="170" y="106" width="10" height="12" rx="3" />
                    <rect className="fill-fill-strong" x="163" y="108" width="8" height="2.6" rx="1.3" />
                    <rect className="fill-fill-strong" x="163" y="113.4" width="8" height="2.6" rx="1.3" />
                </g>
            </g>
            <path className="ilustracao-respira fill-none stroke-label-tertiary/45 [stroke-linecap:round] [stroke-width:1.5]" d="M140 102 L148 98 M142 112 L151 112 M140 122 L148 126" />
        </Ilustracao>
    );
}

/** Versão nova: o anel que carrega até completar e o ícone de atualizar que gira uma vez ao fim. */
export function AnelDeAtualizacao() {
    return (
        <Ilustracao>
            <circle className="ilustracao-onda fill-none stroke-green [stroke-width:1.5]" cx="140" cy="96" r="72" />
            <circle className="ilustracao-onda [animation-delay:1.6s] fill-none stroke-green [stroke-width:1.5]" cx="140" cy="96" r="72" />
            <circle className="ilustracao-sombra fill-surface" cx="140" cy="96" r="66" />
            <circle className="fill-none stroke-fill-strong [stroke-width:6]" cx="140" cy="96" r="56" />
            <circle className="ilustracao-anel fill-none stroke-green [stroke-dasharray:352] [stroke-linecap:round] [stroke-width:6]" cx="140" cy="96" r="56" transform="rotate(-90 140 96)" />
            <g className="ilustracao-gira">
                <circle className="fill-green" cx="140" cy="96" r="34" />
                <g className="fill-none stroke-on-green [stroke-linecap:round] [stroke-linejoin:round] [stroke-width:3.6]">
                    <path d="M126 92 a15 15 0 0 1 26 -6 l3 3" />
                    <path d="M155 81 v8 h-8" />
                    <path d="M154 100 a15 15 0 0 1 -26 6 l-3 -3" />
                    <path d="M125 111 v-8 h8" />
                </g>
            </g>
        </Ilustracao>
    );
}
